'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import type { JobDetail } from '~/types/job-detail';
import { FixedScrollTop } from '../footer/fixed-scroll-top';
import { addFavorite, removeFavorite } from '~/app/actions/favorites-action';
import { getCookieAction } from '~/app/actions/client/get-cookie-action';
import { setCookieAction } from '~/app/actions/client/set-cookie-action';
import Image from 'next/image';

type Props = {
  job: JobDetail;
  isSignedIn: boolean;
  jobApplicantsJobIds: number[];
  isFavorite: boolean;
  favoriteJobIds: number[];
  setIsFavorite: (isFavorite: boolean) => void;
};

export default function JobDetailsFloatingButtons({
  job,
  isSignedIn,
  jobApplicantsJobIds,
  isFavorite,
  favoriteJobIds,
  setIsFavorite,
}: Props) {
  const [isProcessing, setIsProcessing] = useState(false);

  const isMobile = false;
  const applicationRef =
    job.service_type === '求人広告'
      ? 'ft-footer-detail-appform_ad'
      : 'ft-footer-detail-thisjob_application_agent';

  const hasApplied = jobApplicantsJobIds.includes(job.id);
  const favoriteButtonClass = isMobile ? 'u-w150' : 'u-w280';

  // 初期お気に入り状態
  useEffect(() => {
    if (isSignedIn) {
      setIsFavorite(favoriteJobIds.includes(job.id));
    } else {
      (async () => {
        const favoriteJobIdsFromCookie = JSON.parse(
          (await getCookieAction('favorite_job_ids')) || '[]',
        );
        setIsFavorite(favoriteJobIdsFromCookie.includes(job.id));
      })();
    }
  }, [job.id, favoriteJobIds, isSignedIn]);

  // ------------------------------
  // jQuery フローティング制御組み込み
  // ------------------------------
  useEffect(() => {
    const setupFloatingMenu = async () => {
      const jQuery = await import('jquery');
      const $: any = jQuery.default;

      class JobDetailFloatingMenu {
        isFloatingMenuHidden = false;
        showThreshold: number;

        constructor(showThreshold: number) {
          this.showThreshold = showThreshold;
          this.bindEvents();
          this.scrollEvent();
        }

        scrollEvent() {
          $(window).on('scroll', () => {
            if (this.isSlideFormVisible()) {
              $('.c-floating-menu__js, .js-back-to-top').fadeOut(100);
            } else if (
              ($(window as any).scrollTop() ?? 0) >= this.showThreshold &&
              !this.isFloatingMenuHidden
            ) {
              $('.c-floating-menu__js, .js-back-to-top').fadeIn(100);
            } else {
              $('.c-floating-menu__js, .js-back-to-top').fadeOut(100);
            }
          });
        }

        bindEvents() {
          $('.js-back-to-top').on('click', () => {
            $('body, html').animate({ scrollTop: 0 }, 500);
          });

          $('.c-floating-menu__apply .btn-close').on('click', () => {
            $('.c-floating-menu__js').hide();
            this.isFloatingMenuHidden = true;
          });
        }

        isSlideFormVisible() {
          const slideForm = document.querySelector(
            '#horizontal_slide_form_wrapper',
          );
          if (!slideForm) return false;

          const rect = slideForm.getBoundingClientRect();
          const slideFormTop = rect.top;
          const slideFormBottom = rect.bottom;

          return slideFormBottom > 0 && slideFormTop <= window.innerHeight;
        }
      }

      new JobDetailFloatingMenu(300);
    };

    setupFloatingMenu();
  }, []);

  // ------------------------------
  // お気に入り登録・解除処理
  // ------------------------------
  const toggleFavorite = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      if (isSignedIn) {
        // ログインしている場合はDBで管理
        if (isFavorite) {
          // お気に入り登録済みの場合は削除
          await removeFavorite(job.id);
          setIsFavorite(false);
        } else {
          // お気に入り登録未済の場合は追加
          await addFavorite(job.id);
          setIsFavorite(true);
        }
      } else {
        // ログインしていない場合はクッキーで管理
        const favorite_job_ids = JSON.parse(
          (await getCookieAction('favorite_job_ids')) || '[]',
        );

        if (isFavorite) {
          const updatedIds = favorite_job_ids.filter(
            (id: number) => id !== job.id,
          );
          await setCookieAction({
            key: 'favorite_job_ids',
            value: JSON.stringify(updatedIds),
          });
          setIsFavorite(false);
        } else {
          const updatedIds = [...favorite_job_ids, job.id];
          await setCookieAction({
            key: 'favorite_job_ids',
            value: JSON.stringify(updatedIds),
          });
          setIsFavorite(true);
        }
      }
    } catch (err) {
      console.error('お気に入り処理失敗:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  // ------------------------------
  // JSX
  // ------------------------------
  return (
    <div className="c-floating-menu__js" style={{ display: 'none' }}>
      <div className="c-floating-menu__apply">
        <button type="button" className="btn-close absolute top-2 right-2">
          <Image
            alt="閉じるアイコン"
            src="/nextjs/images/icons/btn_white_close.png"
            width={18}
            height={18}
          />
        </button>

        {isSignedIn ? (
          <div className="c-floating-menu__apply__buttons -w-full-m0">
            <button
              className={`${favoriteButtonClass} c-button -border-orange -heart-icon -sp-text-sm ${
                isFavorite ? '-solid -done' : ''
              }`}
              onClick={toggleFavorite}
              disabled={isProcessing}
            >
              {isFavorite ? 'お気に入り登録済' : 'お気に入り登録'}
            </button>

            {hasApplied ? (
              <div
                className={`c-button -lightgray -double mt0 mb0 -disabled ${favoriteButtonClass}`}
              >
                応募済み
              </div>
            ) : (
              <Link
                href={`/mypage/jobs/new?job_id=${job.id}&ref=${applicationRef}`}
                className={`c-button -red ${favoriteButtonClass} -double mt0 mb0 -arrow-right`}
              >
                応募画面へ進む
              </Link>
            )}
          </div>
        ) : (
          <div className="u-mt0 u-mb0 p-job-detail__buttons">
            {job.service_type !== '求人広告' ? (
              <>
                <button
                  className={`c-button -border-orange -heart-icon ${
                    isMobile ? 'u-w50 u-fs-sp8' : 'u-w280'
                  } ${isFavorite ? '-solid -done' : ''}`}
                  onClick={toggleFavorite}
                  disabled={isProcessing}
                >
                  {isFavorite ? 'お気に入り登録済' : 'お気に入り登録'}
                </button>

                <Link
                  href={`/entry_member?job_id=${job.id}&ref=ft-footer-detail-thisjob_inquiry_agent`}
                  className={`c-button -green ${
                    isMobile ? 'u-w120 u-fs11 u-pr8' : 'u-w280'
                  } -double -arrow-right`}
                >
                  {isMobile ? '求人を問い合わせる' : 'この求人を問い合わせる'}
                </Link>

                <Link
                  href={`/entry_member?job_id=${job.id}&ref=ft-footer-detail-thisjob_application_agent`}
                  className={`c-button -red ${
                    isMobile ? 'u-w120 u-fs11 u-pr8' : 'u-w280'
                  } -double -arrow-right`}
                >
                  応募画面へ進む
                </Link>
              </>
            ) : (
              <>
                <button
                  className={`c-button -border-orange -heart-icon ${favoriteButtonClass} ${
                    isFavorite ? '-solid -done' : ''
                  }`}
                  onClick={toggleFavorite}
                  disabled={isProcessing}
                >
                  {isFavorite ? 'お気に入り登録済' : 'お気に入り登録'}
                </button>

                <Link
                  href={`/entry_member?job_id=${job.id}&ref=ft-footer-detail-appform_ad`}
                  className={`c-button -red ${favoriteButtonClass} -double -arrow-right`}
                >
                  応募画面へ進む
                </Link>
              </>
            )}
          </div>
        )}
      </div>

      <FixedScrollTop />
    </div>
  );
}
